/**
 * Proof-of-performance stage — structure-scoped, nested in the site dashboard.
 *
 * Rendered as a child route of SiteDashboardLayout, inside the shared
 * dashboard shell. Reads the site snapshot from the layout context instead of
 * the legacy Immutable structure entity, so it survives dashboard navigation
 * without the null relay-controller crash the standalone page had. 2PDC units
 * report through the newer EnhancedPOPTable; everything else uses the
 * client-side PopGrid.
 *
 * Contains:
 * - SiteDashboardPopView — route child component (default export)
 */
import { Paper } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useUser } from '@sophocles/app';
import { fetchProofOfPerformanceReportsForSiteWithRange } from '@sophocles/entities/ProofOfPerformanceReport';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import EnhancedPOPTable from '@sophocles/views/sites/components/EnhancedPOPTable';
import PopGrid from '@sophocles/views/sites/components/PopGrid';
import { createPopList } from '@sophocles/views/sites/components/popSelectors';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

function SiteDashboardPopView() {
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const dispatch = useDispatch();
  const { user } = useUser();

  const { siteId, customer, selectedStructure, selectedPlant, unit, curfew } = useSiteDashboardLayoutContext();

  const selectPopList = useMemo(() => createPopList(t, tAlarm, siteId), [t, tAlarm, siteId]);
  const pops = useSelector(selectPopList);

  const fetchPOP = useCallback(
    (sId, from, to) => dispatch(fetchProofOfPerformanceReportsForSiteWithRange(sId, from, to)),
    [dispatch],
  );
  const is2PDC = unit?.model?.id == 15;

  if (is2PDC) {
    return (
      <Paper sx={{ mx: 1 }}>
        <EnhancedPOPTable
          customerId={customer.get('id', null)}
          siteId={siteId}
          siteGroupId={selectedPlant.get('id', null)}
          site={selectedStructure}
          siteGroup={selectedPlant}
          user={user}
          curfew={curfew}
        />
      </Paper>
    );
  }

  return (
    <PopGrid
      customer={customer}
      selectedPlant={selectedPlant}
      selectedStructure={selectedStructure}
      siteId={siteId}
      pops={pops}
      user={user}
      onMobile={false}
      fetchPOP={fetchPOP}
    />
  );
}

export default SiteDashboardPopView;
